import React, { useState } from "react";
import OTPPage from "./OTPPage";
import ResetPasswordPage from "./ResetPasswordPage";
import { authService, LoginRequest } from "../../services";

interface LoginFormData {
  email: string;
  password: string;
  rememberPassword: boolean;
}

interface ForgotPasswordData {
  email: string;
}

interface LoginPageProps {
  onLoginSuccess: () => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
  const [formData, setFormData] = useState<LoginFormData>({
    email: "",
    password: "",
    rememberPassword: false,
  });
  const [forgotPasswordData, setForgotPasswordData] =
    useState<ForgotPasswordData>({
      email: "",
    });
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState<"login" | "otp" | "reset">(
    "login"
  );
  const [error, setError] = useState<string>("");
  const [verifiedOTP, setVerifiedOTP] = useState<string>("");

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    if (showForgotPassword) {
      setForgotPasswordData((prev) => ({
        ...prev,
        [name]: value,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const loginData: LoginRequest = {
        email: formData.email,
        password: formData.password,
      };

      const response = await authService.login(loginData);
      console.log("Login successful:", response);

      // Store remember password preference
      if (formData.rememberPassword) {
        localStorage.setItem("rememberPassword", "true");
      } else {
        localStorage.removeItem("rememberPassword");
      }

      onLoginSuccess();
    } catch (error: any) {
      console.error("Login failed:", error);
      setError(
        error.message || "Đăng nhập thất bại. Vui lòng kiểm tra lại thông tin."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      await authService.sendPasswordResetOTP(forgotPasswordData.email);
      console.log("OTP sent successfully");
      setCurrentStep("otp"); // Chuyển sang trang OTP
    } catch (error: any) {
      console.error("Send OTP failed:", error);
      setError(error.message || "Gửi mã OTP thất bại. Vui lòng thử lại.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setCurrentStep("login");
    setShowForgotPassword(false);
    setForgotPasswordData({ email: "" });
    setError("");
  };

  const handleOTPVerified = (otp: string) => {
    setVerifiedOTP(otp);
    setCurrentStep("reset"); // Chuyển sang trang đặt lại mật khẩu
  };

  const handlePasswordReset = () => {
    setCurrentStep("login"); // Quay về trang đăng nhập
    setShowForgotPassword(false); // Đóng form quên mật khẩu
    setForgotPasswordData({ email: "" }); // Reset form data
    setVerifiedOTP(""); // Reset OTP
    setError(""); // Clear any errors
    // Không hiển thị thông báo, chỉ chuyển về trang đăng nhập
  };

  // Render different components based on current step
  if (currentStep === "otp") {
    return (
      <OTPPage
        email={forgotPasswordData.email}
        onOTPVerified={handleOTPVerified}
        onBackToLogin={handleBackToLogin}
      />
    );
  }

  if (currentStep === "reset") {
    return (
      <ResetPasswordPage
        email={forgotPasswordData.email}
        otp={verifiedOTP}
        onPasswordReset={handlePasswordReset}
        onBackToLogin={handleBackToLogin}
      />
    );
  }

  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="w-full max-w-xs">
          {/* Title */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-black mb-2">
              Quên mật khẩu
            </h1>
            <p className="text-sm text-gray-600">
              Nhập email để đặt lại mật khẩu
            </p>
          </div>

          {/* Forgot Password Form */}
          <form onSubmit={handleForgotPassword} className="space-y-4">
            {/* Error Message */}
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {/* Email Input */}
            <div>
              <label
                htmlFor="forgot-email"
                className="block text-sm text-black mb-1"
              >
                Email
              </label>
              <input
                id="forgot-email"
                name="email"
                type="email"
                required
                value={forgotPasswordData.email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-400 rounded text-black"
                placeholder="Email"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded font-medium disabled:opacity-50"
            >
              {isLoading ? "Đang xử lý..." : "Gửi email"}
            </button>
          </form>

          {/* Back to Login */}
          <div className="text-center mt-4">
            <button
              type="button"
              onClick={handleBackToLogin}
              className="text-blue-600 text-sm"
            >
              ← Quay lại đăng nhập
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-xs">
        {/* Title */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-black mb-2">Đăng nhập</h1>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Error Message */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Email Input */}
          <div>
            <label htmlFor="email" className="block text-sm text-black mb-1">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-400 rounded text-black"
              placeholder="Email"
            />
          </div>

          {/* Password Input */}
          <div>
            <label htmlFor="password" className="block text-sm text-black mb-1">
              Mật khẩu
            </label>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                required
                value={formData.password}
                onChange={handleInputChange}
                className="w-full px-3 py-2 pr-10 border border-gray-400 rounded text-black"
                placeholder="Mật khẩu"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showPassword ? (
                  <svg
                    className="h-5 w-5 text-gray-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                    />
                  </svg>
                ) : (
                  <svg
                    className="h-5 w-5 text-gray-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Remember Password Checkbox */}
          <div className="flex items-center">
            <input
              id="rememberPassword"
              name="rememberPassword"
              type="checkbox"
              checked={formData.rememberPassword}
              onChange={handleInputChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label
              htmlFor="rememberPassword"
              className="ml-2 block text-sm text-black"
            >
              Ghi nhớ mật khẩu
            </label>
          </div>

          {/* Forgot Password Link */}
          <div className="text-right">
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-blue-600 text-sm"
            >
              Quên mật khẩu?
            </button>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded font-medium disabled:opacity-50"
          >
            {isLoading ? "Đang xử lý..." : "Đăng nhập"}
          </button>
        </form>

        {/* Sign Up Link */}
        <div className="text-center mt-4">
          <p className="text-sm text-gray-600">Bạn đã có tài khoản chưa? </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
